import type {
  CoverageFacility,
  CoverageNurse,
  CoverageProspect,
} from "@vellum/vellum-api";
import * as maplibregl from "maplibre-gl";
import {
  type GeoJSONSource,
  type LngLatBoundsLike,
  type MapGeoJSONFeature,
} from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { useEffect, useRef, useState } from "react";

import { REGION_PALETTE } from "./colors";
import { circleRing } from "./geo";

// Grayscale vector basemap, no API key. Keeps the pins the loudest thing on the map.
const BASEMAP_STYLE = "https://tiles.openfreemap.org/styles/positron";
/** The style's OpenMapTiles vector source, reused for our own boundary layer. */
const BASEMAP_VECTOR_SOURCE = "openmaptiles";
const CONTINENTAL_US: LngLatBoundsLike = [
  [-125, 24],
  [-66, 50],
];

// Status is told apart by fill lightness and shape, never hue alone.
const INK = "#1c1917";
const OUT_OF_REACH = "#b45309";

/** "group" colors by whatever the side panel groups by (region, company). */
export type FacilityColorBy = "group" | "reach";

export type CoverageLayers = {
  facilities: boolean;
  nurses: boolean;
  reach: boolean;
  prospects: boolean;
};

type Props = {
  facilities: CoverageFacility[];
  nurses: CoverageNurse[];
  /** CMS nursing homes that aren't customers yet. */
  prospects: CoverageProspect[];
  nearestMiles: Map<string, number>;
  reachMiles: number;
  layers: CoverageLayers;
  colorBy: FacilityColorBy;
  /** REGION_PALETTE index per facility id, for colorBy "group". */
  facilityColors: Map<string, number>;
  /** Fit the map to these bounds whenever they change. */
  focus: [[number, number], [number, number]] | null;
};

type FacilityProps = {
  name: string;
  detail: string;
  nearest: string;
  outOfReach: boolean;
  icon: string;
};

type NurseProps = { name: string; detail: string };

/** Hollow triangle: prospects read as "not ours yet" next to filled squares. */
function triangleIcon(): ImageData {
  const size = 22;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  ctx.beginPath();
  ctx.moveTo(size / 2, 3);
  ctx.lineTo(size - 3, size - 4);
  ctx.lineTo(3, size - 4);
  ctx.closePath();
  ctx.fillStyle = "rgba(255, 255, 255, 0.85)";
  ctx.fill();
  ctx.strokeStyle = "#57534e";
  ctx.lineWidth = 2.5;
  ctx.stroke();
  return ctx.getImageData(0, 0, size, size);
}

type ProspectProps = { name: string; detail: string; stats: string };

function prospectCollection(
  prospects: CoverageProspect[],
): GeoJSON.FeatureCollection<GeoJSON.Point, ProspectProps & { beds: number }> {
  return {
    type: "FeatureCollection",
    features: prospects.map((prospect) => ({
      type: "Feature",
      geometry: { type: "Point", coordinates: [prospect.long, prospect.lat] },
      properties: {
        name: prospect.name,
        beds: prospect.beds ?? 0,
        detail: [
          [prospect.city, prospect.state].filter(Boolean).join(", "),
          prospect.chain_name,
        ]
          .filter(Boolean)
          .join(" · "),
        stats: [
          prospect.beds != null ? `${prospect.beds} beds` : null,
          prospect.overall_rating != null
            ? `${prospect.overall_rating}★ CMS rating`
            : null,
          "Not a customer",
        ]
          .filter(Boolean)
          .join(" · "),
      },
    })),
  };
}

function squareIcon(fill: string): ImageData {
  const size = 22;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = fill;
  ctx.strokeStyle = INK;
  ctx.lineWidth = 3;
  ctx.fillRect(3, 3, size - 6, size - 6);
  ctx.strokeRect(3, 3, size - 6, size - 6);
  return ctx.getImageData(0, 0, size, size);
}

function facilityIcon(
  facility: CoverageFacility,
  outOfReach: boolean,
  colorBy: FacilityColorBy,
  facilityColors: Map<string, number>,
): string {
  if (colorBy === "reach")
    return outOfReach ? "facility-out" : "facility-covered";
  const index = facilityColors.get(facility.id);
  return index === undefined ? "facility-covered" : `facility-region-${index}`;
}

function facilityCollection(
  facilities: CoverageFacility[],
  nearestMiles: Map<string, number>,
  reachMiles: number,
  colorBy: FacilityColorBy,
  facilityColors: Map<string, number>,
): GeoJSON.FeatureCollection<GeoJSON.Point, FacilityProps> {
  return {
    type: "FeatureCollection",
    features: facilities.map((facility) => {
      const miles = nearestMiles.get(facility.id) ?? Infinity;
      const outOfReach = miles > reachMiles;
      return {
        type: "Feature",
        geometry: { type: "Point", coordinates: [facility.long, facility.lat] },
        properties: {
          name: facility.name,
          detail: [
            facility.company_name,
            `${facility.city}, ${facility.state}`,
            facility.on_hold ? "On hold" : null,
          ]
            .filter(Boolean)
            .join(" · "),
          nearest: Number.isFinite(miles)
            ? `Nearest nurse: ${miles.toFixed(0)} mi (straight line)`
            : "No nurses in view",
          outOfReach,
          icon: facilityIcon(facility, outOfReach, colorBy, facilityColors),
        },
      };
    }),
  };
}

function nurseCollection(
  nurses: CoverageNurse[],
): GeoJSON.FeatureCollection<GeoJSON.Point, NurseProps> {
  return {
    type: "FeatureCollection",
    features: nurses.map((nurse) => ({
      type: "Feature",
      geometry: { type: "Point", coordinates: [nurse.long, nurse.lat] },
      properties: {
        name: nurse.name,
        detail:
          [nurse.role, ...nurse.capabilities].join(" · ") || "No capabilities",
      },
    })),
  };
}

function reachCollection(
  nurses: CoverageNurse[],
  reachMiles: number,
): GeoJSON.FeatureCollection<GeoJSON.Polygon> {
  return {
    type: "FeatureCollection",
    features: nurses.map((nurse) => ({
      type: "Feature",
      geometry: { type: "Polygon", coordinates: [circleRing(nurse, reachMiles)] },
      properties: {},
    })),
  };
}

function popupHtml(title: string, ...lines: string[]): string {
  const escape = (text: string) =>
    text.replace(
      /[&<>"']/g,
      (char) =>
        ({
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;",
        })[char]!,
    );
  return `<div class="text-sm"><div class="font-semibold text-gray-900">${escape(title)}</div>${lines
    .map((line) => `<div class="text-gray-600">${escape(line)}</div>`)
    .join("")}</div>`;
}

export function CoverageMap({
  facilities,
  nurses,
  prospects,
  nearestMiles,
  reachMiles,
  layers,
  colorBy,
  facilityColors,
  focus,
}: Props) {
  const container = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const map = new maplibregl.Map({
      container: container.current!,
      style: BASEMAP_STYLE,
      bounds: CONTINENTAL_US,
      attributionControl: { compact: true },
    });
    map.addControl(
      new maplibregl.NavigationControl({ showCompass: false }),
      "top-right",
    );
    mapRef.current = map;

    map.on("load", () => {
      map.addImage("facility-covered", squareIcon("#ffffff"));
      map.addImage("facility-out", squareIcon(OUT_OF_REACH));
      REGION_PALETTE.forEach((color, index) =>
        map.addImage(`facility-region-${index}`, squareIcon(color)),
      );

      map.addSource("reach", { type: "geojson", data: reachCollection([], 0) });
      map.addSource("facilities", {
        type: "geojson",
        data: facilityCollection([], new Map(), 0, "reach", new Map()),
      });
      map.addSource("nurses", { type: "geojson", data: nurseCollection([]) });
      map.addSource("prospects", {
        type: "geojson",
        data: prospectCollection([]),
      });
      map.addImage("prospect", triangleIcon());

      // The basemap only draws state lines faintly from z8; we want them at
      // every zoom since regions are planned by state.
      map.addLayer({
        id: "state-lines",
        type: "line",
        source: BASEMAP_VECTOR_SOURCE,
        "source-layer": "boundary",
        filter: [
          "all",
          ["==", ["get", "admin_level"], 4],
          ["!=", ["get", "maritime"], 1],
        ],
        paint: {
          "line-color": "#a8a29e",
          "line-width": ["interpolate", ["linear"], ["zoom"], 3, 0.75, 8, 1.5],
        },
      });
      map.addLayer({
        id: "reach-fill",
        type: "fill",
        source: "reach",
        paint: { "fill-color": INK, "fill-opacity": 0.06 },
      });
      map.addLayer({
        id: "reach-line",
        type: "line",
        source: "reach",
        paint: {
          "line-color": "#78716c",
          "line-width": 1,
          "line-dasharray": [2, 2],
        },
      });
      // Under the facility squares, so our sites stay on top.
      map.addLayer({
        id: "prospects",
        type: "symbol",
        source: "prospects",
        layout: {
          visibility: "none",
          "icon-image": "prospect",
          // Bigger homes draw bigger; beds span roughly 20-300.
          "icon-size": [
            "interpolate",
            ["linear"],
            ["zoom"],
            3,
            [
              "interpolate",
              ["linear"],
              ["get", "beds"],
              20,
              0.3,
              300,
              0.6,
            ],
            9,
            [
              "interpolate",
              ["linear"],
              ["get", "beds"],
              20,
              0.6,
              300,
              1.1,
            ],
          ],
          "icon-allow-overlap": true,
        },
      });
      map.addLayer({
        id: "facilities",
        type: "symbol",
        source: "facilities",
        layout: {
          "icon-image": ["get", "icon"],
          "icon-size": ["interpolate", ["linear"], ["zoom"], 3, 0.45, 9, 0.8],
          "icon-allow-overlap": true,
          // Out-of-reach squares draw on top so they aren't buried in clusters.
          "symbol-sort-key": ["case", ["get", "outOfReach"], 1, 0],
        },
      });
      map.addLayer({
        id: "nurses",
        type: "circle",
        source: "nurses",
        paint: {
          "circle-color": INK,
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 3, 3.5, 9, 7],
          "circle-stroke-color": "#ffffff",
          "circle-stroke-width": 1.5,
        },
      });

      const showPopup = (
        feature: MapGeoJSONFeature | undefined,
        lngLat: maplibregl.LngLat,
        lines: (props: Record<string, string>) => string[],
      ) => {
        if (!feature) return;
        const props = feature.properties as Record<string, string>;
        new maplibregl.Popup({ closeButton: false, maxWidth: "280px" })
          .setLngLat(lngLat)
          .setHTML(popupHtml(props.name, ...lines(props)))
          .addTo(map);
      };
      map.on("click", "facilities", (event) =>
        showPopup(event.features?.[0], event.lngLat, (p) => [
          p.detail,
          p.nearest,
        ]),
      );
      map.on("click", "nurses", (event) =>
        showPopup(event.features?.[0], event.lngLat, (p) => [p.detail]),
      );
      map.on("click", "prospects", (event) =>
        showPopup(event.features?.[0], event.lngLat, (p) => [
          p.detail,
          p.stats,
        ]),
      );
      for (const layer of ["facilities", "nurses", "prospects"]) {
        map.on("mouseenter", layer, () => {
          map.getCanvas().style.cursor = "pointer";
        });
        map.on("mouseleave", layer, () => {
          map.getCanvas().style.cursor = "";
        });
      }

      setReady(true);
    });

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map) return;
    (map.getSource("facilities") as GeoJSONSource).setData(
      facilityCollection(
        facilities,
        nearestMiles,
        reachMiles,
        colorBy,
        facilityColors,
      ),
    );
    (map.getSource("nurses") as GeoJSONSource).setData(nurseCollection(nurses));
    (map.getSource("reach") as GeoJSONSource).setData(
      reachCollection(nurses, reachMiles),
    );
  }, [
    ready,
    facilities,
    nurses,
    nearestMiles,
    reachMiles,
    colorBy,
    facilityColors,
  ]);

  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map) return;
    const visibility = (on: boolean) => (on ? "visible" : "none");
    map.setLayoutProperty("facilities", "visibility", visibility(layers.facilities));
    map.setLayoutProperty("nurses", "visibility", visibility(layers.nurses));
    map.setLayoutProperty("reach-fill", "visibility", visibility(layers.reach));
    map.setLayoutProperty("reach-line", "visibility", visibility(layers.reach));
    map.setLayoutProperty("prospects", "visibility", visibility(layers.prospects));
  }, [ready, layers]);

  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map) return;
    (map.getSource("prospects") as GeoJSONSource).setData(
      prospectCollection(prospects),
    );
  }, [ready, prospects]);

  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map || !focus) return;
    map.fitBounds(focus, { padding: 60, maxZoom: 10, duration: 600 });
  }, [ready, focus]);

  return (
    <div
      ref={container}
      className="h-full w-full"
      role="region"
      aria-label="Map of facilities and nurses"
    />
  );
}
